import "./Drinks.scss";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { safeDate } from "../../utils/safeDate";
import Modal from "react-bootstrap/Modal";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";

import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";

// Admin "Drinks" tab (Send a drink, 2026-10-08). Add, edit and turn off the
// drinks members can send each other, and see every send. Venues comp the drink.
const PAIRS = [
  { sender_gender: "male", recipient_gender: "female", label: "Men to women" },
  { sender_gender: "female", recipient_gender: "female", label: "Women to women" },
  { sender_gender: "female", recipient_gender: "male", label: "Women to men" },
  { sender_gender: "male", recipient_gender: "male", label: "Men to men" },
  // Every answer, including non-binary, not to say and none.
  { sender_gender: "anyone", recipient_gender: "anyone", label: "Anyone to anyone" },
];
const CITIES = [
  { value: "miami", label: "Miami" },
  { value: "nyc", label: "NYC" },
];
const STATUSES = ["sent", "accepted", "declined", "redeemed", "expired"];
const pairKey = (r) => `${r.sender_gender}>${r.recipient_gender}`;

const hourLabel = (h) => {
  if (h === 24 || h === 0) return "Midnight";
  if (h === 12) return "Noon";
  return `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`;
};

const windowLabel = (o) => {
  const cutoff = `before ${hourLabel(o.redeem_cutoff_hour_et ?? 24).toLowerCase()} ET`;
  return o.redeem_scope === "any_night"
    ? `Any night ${cutoff}, from ${hourLabel(o.redeem_start_hour_et ?? 0).toLowerCase()}, for ${o.redeem_days ?? 30} days`
    : `The night it's accepted, ${cutoff}`;
};

const rulesLabel = (rules = []) =>
  rules.map((r) => PAIRS.find((p) => pairKey(p) === pairKey(r))?.label || pairKey(r)).join(", ") || "None";

const EMPTY_FORM = {
  name: "",
  description: "",
  venue_id: "",
  venue_name: "",
  approved_by: "",
  city: "miami",
  rules: [],
  redeem_scope: "accept_night",
  redeem_cutoff_hour_et: 24,
  redeem_start_hour_et: 0,
  redeem_days: 30,
  sort_order: 0,
  active: true,
  image_url: "",
};

function Drinks() {
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [editing, setEditing] = useState(null); // null closed, {} new, offer to edit

  const [gifts, setGifts] = useState([]);
  const [giftTotal, setGiftTotal] = useState(0);
  const [giftPage, setGiftPage] = useState(1);
  const [giftStatus, setGiftStatus] = useState("");
  const [loadingGifts, setLoadingGifts] = useState(true);
  const giftRecords = 25;

  const fetchOffers = useCallback(async () => {
    setLoadingOffers(true);
    try {
      const { data } = await api.get("/admin/drinks/offers");
      setOffers(data?.data || []);
    } catch (err) {
      console.error("Drinks fetch failed:", err);
      toast.error(err?.response?.data?.message || "Could not load drinks");
    } finally {
      setLoadingOffers(false);
    }
  }, []);

  const fetchGifts = useCallback(async () => {
    setLoadingGifts(true);
    try {
      const { data } = await api.get("/admin/drinks/gifts", {
        params: { page: giftPage, records: giftRecords, status: giftStatus || undefined },
      });
      setGifts(data?.data || []);
      setGiftTotal(Number(data?.total) || 0);
    } catch (err) {
      console.error("Drink sends fetch failed:", err);
      setGifts([]);
      setGiftTotal(0);
    } finally {
      setLoadingGifts(false);
    }
  }, [giftPage, giftStatus]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  useEffect(() => {
    fetchGifts();
  }, [fetchGifts]);

  const toggleActive = async (offer) => {
    try {
      const form = new FormData();
      form.append("active", String(!offer.active));
      await api.put(`/admin/drinks/offers/${offer._id}`, form);
      toast.success(offer.active ? "Drink turned off" : "Drink turned on");
      fetchOffers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save the drink");
    }
  };

  const giftPages = Math.max(1, Math.ceil(giftTotal / giftRecords));

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="drinks">
            <div className="drinks__header">
              <h2 className="drinks__title">Drinks</h2>
              <button className="drinks__primary" onClick={() => setEditing({})}>
                Add drink
              </button>
            </div>

            <p className="drinks__hint">
              Members send these to each other from a profile, next to Wink and Cheers. The
              recipient has 72 hours to accept; an accepted drink goes to their wallet with a QR
              the venue scans at the door. The venue comps the drink. A drink only shows to
              senders on its city pill, and only for the sender and recipient genders you allow.
              Turning a drink off hides it from new sends; drinks already sent stay valid.
            </p>

            <div className="drinks__table-wrap">
              <table className="drinks__table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Drink</th>
                    <th>Venue</th>
                    <th>City</th>
                    <th>Who can send it to whom</th>
                    <th>Redeem</th>
                    <th>Sends</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loadingOffers && (
                    <tr>
                      <td colSpan={9} className="drinks__empty">Loading...</td>
                    </tr>
                  )}
                  {!loadingOffers && !offers.length && (
                    <tr>
                      <td colSpan={9} className="drinks__empty">No drinks yet. Add the first one.</td>
                    </tr>
                  )}
                  {!loadingOffers &&
                    offers.map((o) => (
                      <tr key={o._id} className={o.active ? "" : "drinks__row--off"}>
                        <td>
                          {o.image_url ? (
                            <img className="drinks__thumb" src={o.image_url} alt="" />
                          ) : (
                            <div className="drinks__thumb drinks__thumb--empty" />
                          )}
                        </td>
                        <td>
                          <div className="drinks__name">{o.name}</div>
                          {o.description ? <div className="drinks__sub">{o.description}</div> : null}
                        </td>
                        <td>
                          {o.venue_name || "Unknown venue"}
                          {o.approved_by?.trim() ? <div className="drinks__sub">Approved by {o.approved_by.trim()}</div> : null}
                        </td>
                        <td>{CITIES.find((c) => c.value === o.city)?.label || o.city}</td>
                        <td>{rulesLabel(o.rules)}</td>
                        <td className="drinks__sub">{windowLabel(o)}</td>
                        <td className="drinks__stats">
                          {o.stats?.sent || 0} waiting, {o.stats?.accepted || 0} accepted,{" "}
                          {o.stats?.redeemed || 0} redeemed, {o.stats?.declined || 0} declined,{" "}
                          {o.stats?.expired || 0} expired
                        </td>
                        <td>
                          <span className={`drinks__status drinks__status--${o.active ? "on" : "off"}`}>
                            {o.active ? "On" : "Off"}
                          </span>
                        </td>
                        <td className="drinks__actions">
                          <button className="drinks__link" onClick={() => setEditing(o)}>Edit</button>
                          <button className="drinks__link" onClick={() => toggleActive(o)}>
                            {o.active ? "Turn off" : "Turn on"}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="drinks__header drinks__header--sends">
              <h3 className="drinks__subtitle">Sends</h3>
              <select
                className="drinks__select"
                value={giftStatus}
                onChange={(e) => {
                  setGiftStatus(e.target.value);
                  setGiftPage(1);
                }}
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s === "sent" ? "Waiting" : s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="drinks__table-wrap">
              <table className="drinks__table">
                <thead>
                  <tr>
                    <th>Sent</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Drink</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingGifts && (
                    <tr><td colSpan={6} className="drinks__empty">Loading...</td></tr>
                  )}
                  {!loadingGifts && !gifts.length && (
                    <tr><td colSpan={6} className="drinks__empty">No sends yet.</td></tr>
                  )}
                  {!loadingGifts &&
                    gifts.map((g) => (
                      <tr key={g._id}>
                        <td>{safeDate(g.created_at)}</td>
                        <td>{g.sender_name || g.sender_id}</td>
                        <td>{g.recipient_name || g.recipient_id}</td>
                        <td>
                          {g.drink}
                          {g.venue_name ? <div className="drinks__sub">{g.venue_name}</div> : null}
                        </td>
                        <td>
                          <span className={`drinks__status drinks__status--${g.status}`}>
                            {g.status === "sent" ? "Waiting" : g.status}
                          </span>
                        </td>
                        <td className="drinks__sub">{giftUpdatedLabel(g)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {giftPages > 1 && (
              <div className="drinks__pager">
                <button className="drinks__link" disabled={giftPage <= 1} onClick={() => setGiftPage((p) => p - 1)}>
                  Previous
                </button>
                <span>Page {giftPage} of {giftPages}</span>
                <button className="drinks__link" disabled={giftPage >= giftPages} onClick={() => setGiftPage((p) => p + 1)}>
                  Next
                </button>
              </div>
            )}
          </section>
        </article>
      </div>

      <DrinkForm
        offer={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          fetchOffers();
        }}
      />
    </main>
  );
}

function giftUpdatedLabel(g) {
  if (g.status === "redeemed") return `Redeemed ${safeDate(g.redeemed_at)}`;
  if (g.status === "accepted") return `Accepted ${safeDate(g.accepted_at)}, use by ${safeDate(g.redeem_deadline)}`;
  if (g.status === "declined") return `Declined ${safeDate(g.declined_at)}`;
  if (g.status === "expired") return `Expired ${safeDate(g.expired_at)}`;
  return `Accept by ${safeDate(g.expires_at)}`;
}

function DrinkForm({ offer, onClose, onSaved }) {
  const show = !!offer;
  const isEdit = !!offer?._id;
  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [venueSearch, setVenueSearch] = useState("");
  const [venueResults, setVenueResults] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!offer) return;
    setForm({ ...EMPTY_FORM, ...offer, venue_id: offer.venue_id ? String(offer.venue_id) : "" });
    setImage(null);
    setPreview(offer.image_url || "");
    setVenueSearch("");
    setVenueResults([]);
  }, [offer]);

  // Same venue search the Offers form uses.
  useEffect(() => {
    if (!show || venueSearch.trim().length < 2) {
      setVenueResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/venue-owners/venues", {
          params: { search: venueSearch.trim(), records: 20, page: 0 },
        });
        setVenueResults(data?.data || []);
      } catch (err) {
        console.error("Venue search failed:", err);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [venueSearch, show]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hasPair = (p) => form.rules.some((r) => pairKey(r) === pairKey(p));
  // Anyone to anyone already covers every pair, so it stands alone: picking it clears the
  // gender pairs, and picking a gender pair clears it.
  const isAnyone = (r) => r.sender_gender === "anyone" || r.recipient_gender === "anyone";
  const togglePair = (p) =>
    set(
      "rules",
      hasPair(p)
        ? form.rules.filter((r) => pairKey(r) !== pairKey(p))
        : [
            ...form.rules.filter((r) => isAnyone(r) === isAnyone(p)),
            { sender_gender: p.sender_gender, recipient_gender: p.recipient_gender },
          ],
    );

  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i + 1), []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.venue_id) return toast.error("Pick a venue");
    if (!form.rules.length) return toast.error("Pick who can send it to whom");

    const body = new FormData();
    body.append("name", form.name.trim());
    body.append("description", (form.description || "").trim());
    body.append("venue_id", form.venue_id);
    body.append("approved_by", (form.approved_by || "").trim());
    body.append("city", form.city);
    body.append("rules", JSON.stringify(form.rules.map(({ sender_gender, recipient_gender }) => ({ sender_gender, recipient_gender }))));
    body.append("redeem_scope", form.redeem_scope);
    body.append("redeem_cutoff_hour_et", String(form.redeem_cutoff_hour_et));
    body.append("redeem_start_hour_et", String(form.redeem_start_hour_et));
    body.append("redeem_days", String(form.redeem_days));
    body.append("sort_order", String(form.sort_order || 0));
    body.append("active", String(!!form.active));
    if (image) body.append("image", image);

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/drinks/offers/${offer._id}`, body);
        toast.success("Drink saved");
      } else {
        await api.post("/admin/drinks/offers", body);
        toast.success("Drink added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save the drink");
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <form className="drinks__form" onSubmit={save}>
        <div className="drinks__form-header">
          <div className="drinks__form-title">{isEdit ? "Edit drink" : "Add drink"}</div>
          <button type="button" className="drinks__close" onClick={onClose} aria-label="Close">
            <MdClose size={20} />
          </button>
        </div>

        <div className="drinks__form-body">
          <label className="drinks__field">
            <span>Name</span>
            <input value={form.name} maxLength={80} onChange={(e) => set("name", e.target.value)} placeholder="Welcome drink" />
          </label>

          <label className="drinks__field">
            <span>Description (shown to members)</span>
            <textarea value={form.description} maxLength={300} rows={2} onChange={(e) => set("description", e.target.value)} />
          </label>

          <div className="drinks__field">
            <span>Venue</span>
            {form.venue_id ? (
              <div className="drinks__picked">
                {form.venue_name || form.venue_id}
                <button type="button" className="drinks__link" onClick={() => setForm((f) => ({ ...f, venue_id: "", venue_name: "", approved_by: "" }))}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <input value={venueSearch} onChange={(e) => setVenueSearch(e.target.value)} placeholder="Search venues" />
                {venueResults.length > 0 && (
                  <div className="drinks__results">
                    {venueResults.map((v) => (
                      <button
                        type="button"
                        key={v._id}
                        className="drinks__result"
                        onClick={() => setForm((f) => ({ ...f, venue_id: String(v._id), venue_name: v.name, approved_by: "" }))}
                      >
                        {v.name}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <label className="drinks__field">
            <span>Approved by (shown on new passes)</span>
            <input
              value={form.approved_by || ""}
              maxLength={80}
              onChange={(e) => set("approved_by", e.target.value)}
              placeholder="Who at the venue approved it"
            />
          </label>

          <div className="drinks__field">
            <span>Image</span>
            <div className="drinks__image-row">
              {preview ? <img className="drinks__thumb drinks__thumb--large" src={preview} alt="" /> : null}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setImage(file);
                  setPreview(URL.createObjectURL(file));
                }}
              />
            </div>
          </div>

          <label className="drinks__field">
            <span>City pill</span>
            <select value={form.city} onChange={(e) => set("city", e.target.value)}>
              {CITIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>

          <div className="drinks__field">
            <span>Who can send it to whom</span>
            <div className="drinks__checks">
              {PAIRS.map((p) => (
                <label key={pairKey(p)} className="drinks__check">
                  <input type="checkbox" checked={hasPair(p)} onChange={() => togglePair(p)} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>

          <div className="drinks__field">
            <span>Redemption window (Eastern time)</span>
            <div className="drinks__checks">
              <label className="drinks__check">
                <input type="radio" checked={form.redeem_scope === "accept_night"} onChange={() => set("redeem_scope", "accept_night")} />
                The night it's accepted
              </label>
              <label className="drinks__check">
                <input type="radio" checked={form.redeem_scope === "any_night"} onChange={() => set("redeem_scope", "any_night")} />
                Any night, for a number of days
              </label>
            </div>
            <div className="drinks__inline">
              <label>
                Redeem before
                <select value={form.redeem_cutoff_hour_et} onChange={(e) => set("redeem_cutoff_hour_et", Number(e.target.value))}>
                  {hours.map((h) => (
                    <option key={h} value={h}>{hourLabel(h)}</option>
                  ))}
                </select>
              </label>
              {form.redeem_scope === "any_night" && (
                <>
                  <label>
                    From
                    <select value={form.redeem_start_hour_et} onChange={(e) => set("redeem_start_hour_et", Number(e.target.value))}>
                      {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                        <option key={h} value={h}>{hourLabel(h)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    For
                    <input type="number" min={1} max={365} value={form.redeem_days} onChange={(e) => set("redeem_days", Number(e.target.value) || 1)} />
                    days
                  </label>
                </>
              )}
            </div>
          </div>

          <div className="drinks__inline">
            <label>
              Order
              <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", Number(e.target.value) || 0)} />
            </label>
            <label className="drinks__check">
              <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />
              On (members can send it)
            </label>
          </div>
        </div>

        <div className="drinks__form-footer">
          <button type="button" className="drinks__secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="drinks__primary" disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Save" : "Add drink"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default Drinks;
